import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { CenterModal } from "../CenterModal";
import { trpc } from "~/utils/trpc";
import telegramIcon from "~/assets/icons/telegram.svg";

interface TelegramGuideModalProps {
  open: boolean;
  onClose: () => void;
  teamId: string;
}

/**
 * Walks the user through adding the cros-air bot to a Telegram group. Once
 * the bot joins, the my_chat_member webhook auto-creates the project — so
 * this modal is informational only; closing it has no destructive effect.
 */
export default function TelegramGuideModal({
  open,
  onClose,
  teamId,
}: TelegramGuideModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const botInfoQuery = trpc.onboarding.getTelegramBotInfo.useQuery(
    { teamId },
    { enabled: open && Boolean(teamId), retry: 1 },
  );

  const botUsername = botInfoQuery.data?.botUsername ?? null;
  const addToGroupUrl = botInfoQuery.data?.addToGroupUrl ?? null;

  const handleCopyUsername = async () => {
    if (!botUsername) return;
    try {
      await navigator.clipboard.writeText(`@${botUsername}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Older browsers / locked-down embed contexts — silently ignore.
    }
  };

  const handleOpenTelegram = () => {
    if (!addToGroupUrl) return;
    window.open(addToGroupUrl, "_blank", "noopener,noreferrer");
  };

  const stepNumberSx = {
    minWidth: 24,
    height: 24,
    borderRadius: "50%",
    bgcolor: theme.palette.custom.primary,
    color: theme.palette.custom.gray[0],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
  } as const;

  const renderStep = (n: number, label: string) => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={stepNumberSx}>{n}</Box>
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: 1.5,
          color: theme.palette.custom.gray[800],
          flex: 1,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={t("telegram.guide.title")}
      size="md"
      width={480}
    >
      <Stack spacing={2.5} sx={{ pb: 1 }}>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: theme.palette.custom.gray[700],
          }}
        >
          {t("telegram.guide.intro")}
        </Typography>

        <Stack spacing={1.5}>
          {renderStep(1, t("telegram.guide.step1"))}
          {renderStep(2, t("telegram.guide.step2"))}
          {renderStep(3, t("telegram.guide.step3"))}
        </Stack>

        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenTelegram}
          disabled={!addToGroupUrl || botInfoQuery.isLoading}
          startIcon={
            <Box
              component="img"
              src={telegramIcon}
              alt=""
              sx={{ width: 20, height: 20 }}
            />
          }
          sx={{
            minHeight: 52,
            borderRadius: "8px",
            bgcolor: theme.palette.custom.primary,
            fontWeight: 600,
            fontSize: 16,
            textTransform: "none",
            "&:hover": { bgcolor: theme.palette.custom.primaryHover },
            "&.Mui-disabled": {
              bgcolor: theme.palette.custom.primaryDisabled,
              color: theme.palette.custom.gray[0],
            },
          }}
        >
          {t("telegram.guide.openButton")}
        </Button>

        {botUsername && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 1.5,
              borderRadius: "8px",
              bgcolor: theme.palette.custom.gray[100],
              border: "1px solid",
              borderColor: theme.palette.custom.divider,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  color: theme.palette.custom.gray[600],
                  fontWeight: 500,
                }}
              >
                {t("telegram.guide.botUsernameLabel")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: "monospace",
                  color: theme.palette.custom.gray[800],
                  fontWeight: 600,
                }}
              >
                @{botUsername}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCopyUsername}
              aria-label={copied ? t("telegram.guide.copied") : t("telegram.guide.copy")}
              sx={{
                color: copied
                  ? theme.palette.custom.primary
                  : theme.palette.custom.gray[600],
              }}
            >
              {copied ? (
                <CheckIcon fontSize="small" />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        )}

        {botInfoQuery.isError && (
          <Typography
            sx={{
              fontSize: 13,
              color: theme.palette.custom.destructive,
            }}
          >
            {t("telegram.guide.unavailable")}
          </Typography>
        )}

        <Button
          variant="text"
          onClick={onClose}
          sx={{
            alignSelf: "flex-end",
            textTransform: "none",
            fontWeight: 600,
            color: theme.palette.custom.primary,
          }}
        >
          {t("telegram.guide.close")}
        </Button>
      </Stack>
    </CenterModal>
  );
}
